import { DataSourceStatusCode } from '@/data-viz/data-source.interface';
import type { BusinessIntelligenceQueryReason } from '@/interfaces/BusinessIntelligence.interface';
import { type BusinessIntelligenceDimensionQuery } from '@/interfaces/BusinessIntelligence.interface';
import type { IconName } from '@/ui/icon/ui-icon';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import {
    ChangeDetectionStrategy,
    Component,
    type ComponentRef,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    inputBinding,
    output,
    outputBinding,
    type Provider,
    signal,
    viewChild,
    ViewContainerRef
} from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { combineLatest, NEVER, type Observable, throwError } from 'rxjs';
import { catchError, debounceTime, map, take } from 'rxjs/operators';
import { CardShellComponent } from '../../data-viz/data-viz-widgets/shells/card/card-shell';
import type { BusinessIntelligenceWidgetConfiguration } from '../../interfaces/TailoredWidget.interface';
import {
    BusinessIntelligenceRepresentationsProvider,
    type MembersMetaData
} from '../../providers/business-intelligence/business-intelligence-representations.provider';
import { BusinessIntelligenceProvider } from '../../providers/business-intelligence/business-intelligence.provider';
import { CsvExportProvider } from '../../providers/csv-export/csv-export';
import { FrontError } from '../../providers/errors.constants';
import { AdvizeoLoadingService } from '../../services/advizeo-loading.service';
import type { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-data-representations/business-intelligence-representation-component.class';
import { BUSINESS_INTELLIGENCE_REPRESENTATION_CONFIG } from '../business-intelligence-data-representations/business-intelligence-representation-config.token';
import type { BusinessIntelligenceRepresentationData } from '../business-intelligence-data-representations/business-intelligence-representation-data.interface';
import { BUSINESS_INTELLIGENCE_REPRESENTATION_DATA } from '../business-intelligence-data-representations/business-intelligence-representation-data.token';
import { LoadingAndErrorComponent } from '../loading-and-error/loading-and-error';

interface RepresentationPagination {
    limit: number;
    pageCount: number;
}

interface QueryConfiguration {
    dataSource: BusinessIntelligenceWidgetConfiguration['dataSource'];
    representation: BusinessIntelligenceWidgetConfiguration['representation'];
}

interface QueryParams {
    queryConfig: QueryConfiguration;
    context: Record<string, any>;
    metaData: MembersMetaData;
    pagination: RepresentationPagination | null;
}

/** Marks a query rejected because the datasource module is not enabled on the project. */
class ModuleNotSupportedError extends Error {}

@Component({
    // The dynamically spawned shell and representations are legacy `Eager` components that rely on
    // being traversed by change detection, so this host cannot be `OnPush`.
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-widget-spawner.scss'],
    selector: 'business-intelligence-widget-spawner',
    templateUrl: 'business-intelligence-widget-spawner.html',
    imports: [LoadingAndErrorComponent]
})
export class BusinessIntelligenceWidgetSpawnerComponent {
    private _businessIntelligenceProvider = inject(BusinessIntelligenceProvider);
    private _representationsProvider = inject(BusinessIntelligenceRepresentationsProvider);
    private _csvExportProvider = inject(CsvExportProvider);
    private _loadingService = inject(AdvizeoLoadingService);
    private _translate = inject(TranslateService);
    private _injector = inject(Injector);
    private _destroyRef = inject(DestroyRef);

    config = input.required<BusinessIntelligenceWidgetConfiguration>();
    context = input.required<Record<string, any>>();
    position = input<{ x: number; y: number }>({ x: 0, y: 0 });
    buttons = input<
        {
            iconName: IconName;
            text: string;
            click: () => void;
        }[]
    >([]);
    isStatic = input(false);
    reason = input.required<BusinessIntelligenceQueryReason>();

    clickAction = output<any>();
    dataPointCount = output<number>();

    private _shellHost = viewChild.required('shellHost', { read: ViewContainerRef });
    private _representationHost = viewChild.required('representationHost', { read: ViewContainerRef });
    private _dataRepresentationElement = viewChild.required('dataRepresentation', { read: ElementRef<HTMLElement> });

    private _pagination = signal<RepresentationPagination | null>(null, { equal: isEqual });

    private _queryConfig = computed<QueryConfiguration | null>(
        () => {
            const config = this.config() as BusinessIntelligenceWidgetConfiguration | undefined;
            return isNullish(config) ? null : { dataSource: config.dataSource, representation: config.representation };
        },
        { equal: isEqual }
    );

    /** Deep equality avoids refetching when the parent rebinds an equivalent context object. */
    private _stableContext = computed(() => this.context(), { equal: isEqual });

    private _metaData = rxResource({
        params: () => this._queryConfig()?.dataSource.dataSourceCode,
        stream: ({ params: dataSourceCode }) =>
            combineLatest([
                this._businessIntelligenceProvider.getBusinessIntelligenceDimensionsByDataSource$(dataSourceCode),
                this._businessIntelligenceProvider.getBusinessIntelligenceMeasuresByDataSource$(dataSourceCode)
            ]).pipe(map(([dimensions, measures]): MembersMetaData => ({ dimensions, measures })))
    });

    private _data = rxResource<BusinessIntelligenceRepresentationData | null, QueryParams | undefined>({
        params: (): QueryParams | undefined => {
            const queryConfig = this._queryConfig();
            const context = this._stableContext();
            const metaData = this._metaData.hasValue() ? this._metaData.value() : null;
            if (isNullish(queryConfig) || isNullish(queryConfig.representation) || isNullish(context) || isNullish(metaData)) {
                return undefined;
            }
            return { queryConfig, context, metaData, pagination: this._pagination() };
        },
        stream: ({ params }) => this._queryRepresentationData$(params),
        defaultValue: null
    });

    /** `null` until a result is available: also acts as the reset when the query is re-run. */
    representationData = computed(() => (this._data.hasValue() ? this._data.value() : null));

    hasError = computed(() => isNotNullish(this._data.error()) || isNotNullish(this._metaData.error()));
    loading = computed(() => !this.hasError() && isNullish(this.representationData()));
    noData = computed(() => this.representationData()?.data.length === 0);

    errorMessage = computed(() =>
        this._data.error() instanceof ModuleNotSupportedError
            ? (this._translate.instant('TAILORED_WIDGET.BUSINESS_INTELLIGENCE.ERRORS.MODULE_NOT_SUPPORTED') as string)
            : undefined
    );

    isRepresentationVisible = computed(() => (this.representationData()?.data.length ?? 0) > 0);

    private _representationComponent = computed(() => {
        const representation = this._queryConfig()?.representation;
        return isNullish(representation)
            ? null
            : this._representationsProvider.getRepresentationComponent(representation.representationCode);
    });

    private _representationConfig = computed(() => {
        const representation = this._queryConfig()?.representation;
        return isNullish(representation)
            ? null
            : this._representationsProvider.getComponentConfigFromRepresentationConfig(
                  representation.representationCode,
                  representation.representationConfig
              );
    });

    private _shellConfig = computed(
        () => {
            const shell = this.config()?.shell;
            if (isNullish(shell)) {
                return null;
            }
            return {
                title: this._translate.instant('COMPONENTS.BI_WIDGET.DEFAULT_TITLE'),
                legacy: true,
                export: true,
                ...shell.shellConfig
            };
        },
        { equal: isEqual }
    );

    private _hasShell = computed(() => isNotNullish(this._shellConfig()));

    /** `null` while no result is available yet: there is nothing to report to the parent. */
    private _dataPointCount = computed(() => {
        if (this.hasError()) {
            return 0;
        }
        const data = this.representationData();
        return isNullish(data) ? null : (data.elementCount ?? data.data.length);
    });

    // The representations inject their data and config through the legacy observable-based tokens.
    private _representationProviders: Provider[] = [
        { provide: BUSINESS_INTELLIGENCE_REPRESENTATION_DATA, useValue: toObservable(this.representationData) },
        { provide: BUSINESS_INTELLIGENCE_REPRESENTATION_CONFIG, useValue: toObservable(this._representationConfig) }
    ];

    private _representationRef?: ComponentRef<BusinessIntelligenceRepresentationComponent<any>>;

    constructor() {
        effect(onCleanup => {
            const representationComponent = this._representationComponent();
            if (isNullish(representationComponent)) {
                return;
            }
            // Output subscriptions declared via `outputBinding` are torn down automatically on `ref.destroy()`.
            const ref = this._representationHost().createComponent(representationComponent, {
                injector: Injector.create({ providers: this._representationProviders, parent: this._injector }),
                bindings: [
                    outputBinding<RepresentationPagination | null>('paginationRequested', pagination => this._pagination.set(pagination)),
                    outputBinding('clickEvent', event => this.clickAction.emit(event))
                ]
            });
            this._representationRef = ref;
            // The representation is recreated when the component type changes,
            // which happens when the representation code changes.
            onCleanup(() => {
                ref.destroy();
                this._representationRef = undefined;
            });
        });
        // create the shell
        effect(onCleanup => {
            if (!this._hasShell()) {
                return;
            }
            const ref = this._shellHost().createComponent(CardShellComponent, {
                projectableNodes: [[this._dataRepresentationElement().nativeElement]],
                bindings: [
                    inputBinding('config', this._shellConfig),
                    inputBinding('buttons', this.buttons),
                    inputBinding('status', () => ({ code: DataSourceStatusCode.OK })),
                    inputBinding('customInputs', () => []),
                    outputBinding('exportRequested', () => this.exportRepresentationAsCsv())
                ]
            });
            onCleanup(() => ref.destroy());
        });

        effect(() => {
            const count = this._dataPointCount();
            if (isNotNullish(count)) {
                this.dataPointCount.emit(count);
            }
        });
    }

    exportRepresentationAsCsv() {
        const representation = this._representationRef?.instance;
        if (isNullish(representation)) {
            return;
        }
        const spinner = this._loadingService.show(this._translate.instant('STUFF.LOADING'));
        representation
            .exportAsCsv$()
            .pipe(
                debounceTime(500),
                take(1),
                catchError(error => {
                    console.error(error);
                    spinner.dismiss();
                    return NEVER;
                }),
                takeUntilDestroyed(this._destroyRef)
            )
            .subscribe(({ data, columns }) => {
                spinner.dismiss();
                this._csvExportProvider.downloadCsvFromDataSource(data, columns);
            });
    }

    private _queryRepresentationData$({
        queryConfig: { dataSource, representation },
        context,
        metaData,
        pagination
    }: QueryParams): Observable<BusinessIntelligenceRepresentationData> {
        const { representationCode, representationConfig } = representation;
        const configuredLimit = this._representationsProvider.getLimitFromRepresentationConfig(representationCode, representationConfig);
        const paginationOverride = configuredLimit === null ? pagination : null;

        return this._businessIntelligenceProvider
            .queryBusinessIntelligenceDataSourceForRepresentation$(
                {
                    dataSourceCode: dataSource.dataSourceCode,
                    measures: this._representationsProvider.getMeasureCodesFromRepresentationConfig(
                        representationCode,
                        representationConfig
                    ),
                    dimensions: this._representationsProvider.getDimensionsFromRepresentationConfig(
                        representationCode,
                        representationConfig
                    ) as BusinessIntelligenceDimensionQuery[],
                    filters: dataSource.filters,
                    sort: this._representationsProvider.getSortFromRepresentationConfig(representationCode, representationConfig, metaData),
                    limit: paginationOverride?.limit ? paginationOverride.limit : configuredLimit,
                    pageCount: paginationOverride?.pageCount ?? null,
                    shouldNotFilterNullResult:
                        this._representationsProvider.getShouldFilterNullResultFromRepresentationConfig(
                            representationCode,
                            representationConfig
                        ) ?? undefined
                },
                context,
                this.position().y,
                this.reason(),
                this.isStatic()
            )
            .pipe(
                catchError((error: unknown) => {
                    if (error === FrontError.MODULE_NOT_SUPPORTED) {
                        console.error(`Module for datasource "${dataSource.dataSourceCode}" is not enabled on this project`);
                        return throwError(() => new ModuleNotSupportedError());
                    }
                    console.error(error);
                    return throwError(() => error);
                })
            );
    }
}
